import React, { useState } from "react";
import { Sparkles, ArrowDownWideNarrow, Lightbulb, GraduationCap, Layers, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useTranslation } from "@/context/LanguageContext";

const FOLLOWUPS = [
    { id: "simpler", icon: Sparkles },
    { id: "deeper", icon: ArrowDownWideNarrow },
    { id: "examples", icon: Lightbulb },
    { id: "examQ", icon: GraduationCap },
];

// Quick actions under the latest complete AI answer. The first four send a
// language-aware follow-up prompt; "Save as flashcard" asks the AI to distil the
// answer into ONE card, shows a preview, then saves a REAL, deduplicated card.
export const ChatQuickActions = ({ question, answer, onFollowUp, makeCardFn, saveCardFn }) => {
    const { t } = useTranslation();
    const [card, setCard] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const makeCard = async () => {
        setLoading(true);
        try {
            const c = await makeCardFn({ question, answer });
            setCard(c);
        } catch (e) {
            toast.error(t("chat.card.failed"));
        } finally {
            setLoading(false);
        }
    };

    const save = async () => {
        if (!card) return;
        setSaving(true);
        try {
            const r = await saveCardFn({ front: card.front, back: card.back, hint: card.hint || undefined, topic: card.topic || "Chat", type: card.type || "basic", source_hash: card.source_hash });
            if (r?.duplicate) toast.info(t("chat.card.duplicate"));
            else toast.success(t("chat.card.saved"));
            setCard(null);
        } catch (e) {
            toast.error(t("chat.card.failed"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mt-3 border-t border-border/60 pt-2.5" data-testid="chat-quick-actions">
            <div className="flex flex-wrap gap-1.5">
                {FOLLOWUPS.map((a) => (
                    <button
                        key={a.id}
                        data-testid={`chat-quick-${a.id}`}
                        onClick={() => onFollowUp(t(`chat.quickPrompt.${a.id}`))}
                        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-3/50 px-2.5 py-1 text-xs text-t2 transition-colors hover:border-[hsl(var(--primary))]/50 hover:text-t1"
                    >
                        <a.icon className="h-3.5 w-3.5" /> {t(`chat.quick.${a.id}`)}
                    </button>
                ))}
                <button
                    data-testid="chat-quick-flashcard"
                    onClick={makeCard}
                    disabled={loading || saving}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/10 px-2.5 py-1 text-xs font-medium text-[hsl(var(--primary))] transition-colors hover:bg-[hsl(var(--primary))]/15 disabled:opacity-60"
                >
                    {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Layers className="h-3.5 w-3.5" />} {t("chat.quick.flashcard")}
                </button>
            </div>

            {card && (
                <div className="mt-2 rounded-xl border border-[hsl(var(--primary))]/25 bg-surface-2/60 p-3" data-testid="chat-card-preview">
                    <div className="mono-label mb-1.5 flex items-center gap-1 text-[hsl(var(--primary))]">
                        <Layers className="h-3 w-3" /> {t("chat.card.preview")}
                    </div>
                    <div className="space-y-1.5 text-sm">
                        <div>
                            <div className="mono-label text-t4">{t("chat.card.front")}</div>
                            <p data-testid="chat-card-front" className="text-t1">{card.front}</p>
                        </div>
                        <div>
                            <div className="mono-label text-t4">{t("chat.card.back")}</div>
                            <p data-testid="chat-card-back" className="leading-relaxed text-t2">{card.back}</p>
                        </div>
                    </div>
                    {card.duplicate && <p className="mt-2 text-[11px] text-[hsl(var(--warning))]">{t("chat.card.duplicate")}</p>}
                    <div className="mt-2.5 flex justify-end gap-2">
                        <Button data-testid="chat-card-cancel" size="sm" variant="ghost" onClick={() => setCard(null)}>{t("chat.card.cancel")}</Button>
                        <Button data-testid="chat-card-save" size="sm" disabled={saving} onClick={save} className="gap-1.5">
                            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} {t("chat.card.save")}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};
